import React from "react";
import useModelTagsManager from "domains/models/hooks/useModelTagsManager";
import TagDisplayWithRemove from "domains/tags/components/TagDisplayWithRemove";
import TagInput from "domains/tags/components/TagInput";
import TagSuggestion from "domains/tags/components/TagSuggestion";
import { responsiveMaxTagBreakpoints } from "domains/tags/constants/tags";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import MarkdownViewer from "domains/ui/components/Markdown/Viewer";
import useResponsiveBreakpoints from "domains/ui/hooks/useResponsiveBreakpoints";
import {
  GetModelsByModelIdApiResponse,
  useGetModelsDescriptionByModelIdQuery,
} from "infra/api/generated/api";

import {
  Box,
  Center,
  Flex,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";

interface DescriptionSectionProps {
  isOnLoraCatalog: boolean;
  model: GetModelsByModelIdApiResponse["model"];
  handleOnSeeMore: () => void;
}

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  isOnLoraCatalog,
  handleOnSeeMore,
  model,
}) => {
  const { selectedProject } = useTeamContext();
  const { addTags, removeTag, updateTags } = useModelTagsManager();
  const maxTags = useResponsiveBreakpoints(responsiveMaxTagBreakpoints);
  const isModelOwned = model?.ownerId === selectedProject.id;
  const { data: modelDescription, isLoading: isLoadingModelDescription } =
    useGetModelsDescriptionByModelIdQuery({
      projectId: selectedProject.id,
      modelId: model.id,
    });

  return (
    <Flex direction="column" w={["40%", "35%", "30%"]}>
      <Text size="body.bold.md">Description</Text>
      {isLoadingModelDescription ? (
        <Center as={VStack} w="full" pt={2}>
          <SkeletonText w="100%" noOfLines={3} skeletonHeight={4} />
        </Center>
      ) : (
        <Box w="full" py={1}>
          {modelDescription?.description && (
            <MarkdownViewer
              {...modelDescription.description}
              emptyState="No description provided."
              onSeeMore={handleOnSeeMore}
              isOnLoraCatalog={isOnLoraCatalog}
              isTruncated
            />
          )}
        </Box>
      )}
      <Flex direction="column" gap={2} pt={2}>
        <Text pb={1} size="body.bold.md">
          Tags
        </Text>
        <TagDisplayWithRemove
          tags={model.tags}
          maxTags={maxTags}
          onSeeMore={handleOnSeeMore}
          onRemove={
            isModelOwned
              ? (tagToRemove) => removeTag(model, tagToRemove)
              : undefined
          }
        />
        {isModelOwned && (
          <>
            <TagInput
              currentTags={model.tags}
              onAddTags={(tags: string[]) => addTags(model, tags)}
            />
            <TagSuggestion
              tags={model.tags}
              onUpdateTags={(newTags: string[]) => updateTags(model, newTags)}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
